/**
 * Credits Progress Bar
 *
 * Visual progress indicator showing how close the user is to the unlock
 * threshold for their store credits. Three visual states:
 *   - hidden:   feature disabled, user not logged in, or threshold not met
 *   - progress: cart subtotal below threshold — shows fill bar + remaining
 *   - unlocked: cart subtotal >= threshold — credits ready to use at checkout
 */

'use client';

import { formatPrice, type FormatPriceOptions, SafeHtml } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n';
import {
  CART_CREDITS_PROGRESS_LABEL,
  CART_CREDITS_PROGRESS_PREFIX,
  CART_CREDITS_PROGRESS_SUFFIX,
  CART_CREDITS_UNLOCKED,
} from './i18.keys';
import {
  ProgressBarContainer,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressBarLabels,
  ProgressBarLabel,
  ProgressBarMessage,
  ProgressBarMessageBold,
} from './CartSummaryV2.styled';
import type { CreditsProgressState } from '@/lib/hooks/use-credits';

export interface CreditsProgressBarProps {
  credits: CreditsProgressState;
  priceOptions: FormatPriceOptions;
}

export function CreditsProgressBar({
  credits,
  priceOptions,
}: Readonly<CreditsProgressBarProps>) {
  const { t } = useTranslation('sidecart');

  if (!credits.isEnabled) {
    return null;
  }

  const currentFormatted = formatPrice(
    credits.unlockThreshold - credits.remaining,
    priceOptions,
  );
  const thresholdFormatted = formatPrice(credits.unlockThreshold, priceOptions);
  const remainingFormatted = formatPrice(credits.remaining, priceOptions);
  // 1 credit = $1.00 — display as dollar amount
  const balanceFormatted = formatPrice(credits.balance * 100, priceOptions);

  if (credits.isUnlocked) {
    return (
      <ProgressBarContainer data-testid="credits-bar">
        <ProgressBarTrack>
          <ProgressBarFill $percent={100} $unlocked />
        </ProgressBarTrack>
        <ProgressBarLabels>
          <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
          <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
        </ProgressBarLabels>
        <SafeHtml
          as={ProgressBarMessage}
          html={t(CART_CREDITS_UNLOCKED, { balance: balanceFormatted })}
          data-testid="credits-bar-unlocked"
        />
      </ProgressBarContainer>
    );
  }

  return (
    <ProgressBarContainer data-testid="credits-bar">
      <ProgressBarTrack>
        <ProgressBarFill $percent={credits.percentage} />
      </ProgressBarTrack>
      <ProgressBarLabels>
        <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
        <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
      </ProgressBarLabels>
      <ProgressBarMessage data-testid="credits-bar-progress">
        {t(CART_CREDITS_PROGRESS_PREFIX)}{' '}
        <ProgressBarMessageBold>{remainingFormatted}</ProgressBarMessageBold>{' '}
        {t(CART_CREDITS_PROGRESS_SUFFIX)}{' '}
        <ProgressBarMessageBold>{balanceFormatted}</ProgressBarMessageBold>{' '}
        {t(CART_CREDITS_PROGRESS_LABEL)}
      </ProgressBarMessage>
    </ProgressBarContainer>
  );
}
